/**
 * RunMapPage — replaces NavCtrl + templates/nav.html, the Run map of the runsheet's stops.
 *
 * AngularJS → React mapping:
 *   Mapbox map             → Google map from @vis.gl/react-google-maps
 *   locate() (undefined)   → Locate pans to useRunsheetLocation() at street zoom
 *   Mapbox zoom control    → our own 44px Zoom in and Zoom out buttons
 *   numbered status circles → RunMapMarkers, in the runsheet's status colours
 *   Edit Display Order popup → MapSheet, with Move to Order # and Waze
 *
 * The stops, the run line, the driver and the transfer stations are drawn by
 * RunMapMarkers. Stops with no usable coordinates are listed behind a "N stops not on
 * the map" button at the top left instead. Tapping a stop or a station, or a row of
 * that list, opens MapSheet over the map. A stop tap also selects the stop on the
 * Dashboard. Tapping the map or the sheet's close button closes it.
 *
 * AppShell mounts this page on its first visit and then keeps it mounted, so a driver who
 * never opens the map never loads Google's script, and a session costs one billed load.
 * The header matches Chat's in every Size class. The map fills the rest of the shell to
 * its edges, and nothing of ours enters the clear band at its bottom, where Google's
 * attribution sits.
 */

import { useEffect, useRef, useState } from 'react';
import { ActionIcon, Box, Button, Center, Group, Menu, Stack, Text, Title } from '@mantine/core';
import { useElementSize, useMergedRef } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  APIProvider,
  Map as GoogleMap,
  useApiLoadingStatus,
  useMap,
} from '@vis.gl/react-google-maps';
import {
  IconArrowLeft,
  IconChevronDown,
  IconCurrentLocation,
  IconMinus,
  IconPlus,
} from '@tabler/icons-react';

import { HeaderAction } from '@/components/HeaderAction';
import { MapSheet } from '@/components/MapSheet';
import { PULSE_DURATION_MS, RunMapMarkers } from '@/components/RunMapMarkers';
import {
  MAP_CLEAR_BAND,
  MAP_CONTROL_INSET,
  mapSetUp,
  nextStopId,
  panClearBy,
  stopName,
  stopPosition,
  stopsNotOnMap,
} from '@/components/runMap';
import { useRunsheetContext, useRunsheetLocation } from '@/contexts/RunsheetContext';
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_MAP_ID } from '@/env';
import { useBackToDashboard } from '@/hooks/useBackToDashboard';
import { useMoveToOrder } from '@/hooks/useMoveToOrder';
import { useSizeClass } from '@/hooks/useSizeClass';
import type { ReactNode, RefObject } from 'react';
import type { MapSheetItem } from '@/components/MapSheet';
import type { MarkerPulse } from '@/components/RunMapMarkers';
import type { Offset } from '@/components/runMap';
import type { RunsheetLine, Station } from '@/types';

// Brisbane, where every run is. The first open fits the stops instead once they're drawn.
const DEFAULT_CENTER = { lat: -27.47, lng: 153.02 };
const DEFAULT_ZOOM = 11;
const STREET_ZOOM = 17;
// The zoom buttons sit 8px above the clear band, or in Single-pane 16px above an open sheet.
const ZOOM_BUTTONS_BOTTOM = MAP_CLEAR_BAND + MAP_CONTROL_INSET;
const ZOOM_SHEET_GAP = 16;

type AuthFailureWindow = Window & { gm_authFailure?: () => void };

/** What the open sheet shows, and where the tapped marker is, for pan clear. */
interface SheetTarget {
  kind: 'stop' | 'station';
  id: number;
  marker: TappedMarker | null;
}

interface TappedMarker {
  position: google.maps.LatLngLiteral;
  /** Where a fanned-out unit sits, in px from its address. */
  offset?: Offset;
}

/** The open sheet's stop or station, or null when it has gone from the runsheet. */
function sheetItem(
  target: SheetTarget | null,
  lines: RunsheetLine[],
  stations: Station[],
): MapSheetItem | null {
  if (!target) return null;
  if (target.kind === 'station') {
    const station = stations.find(s => s.id === target.id);
    return station ? { kind: 'station', station } : null;
  }
  const line = lines.find(l => l.id === target.id);
  return line ? { kind: 'stop', line, next: line.id === nextStopId(lines) } : null;
}

/** Where `position` is on the map now, in px from its top left corner. */
function mapPixel(map: google.maps.Map, position: google.maps.LatLngLiteral): Offset | null {
  const projection = map.getProjection();
  const bounds = map.getBounds();
  const zoom = map.getZoom();
  if (!projection || !bounds || zoom === undefined) return null;
  const topLeft = projection.fromLatLngToPoint({
    lat: bounds.getNorthEast().lat(),
    lng: bounds.getSouthWest().lng(),
  });
  const point = projection.fromLatLngToPoint(position);
  if (!topLeft || !point) return null;
  const scale = 2 ** zoom;
  return { x: (point.x - topLeft.x) * scale, y: (point.y - topLeft.y) * scale };
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function RunMapPage() {
  // With no key there is no script to load, so the page skips the provider.
  if (!GOOGLE_MAPS_API_KEY) return <RunMapScreen />;
  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
      <RunMapScreen />
    </APIProvider>
  );
}

// ── Screen ────────────────────────────────────────────────────────────────────

function RunMapScreen() {
  const handleBack = useBackToDashboard();
  const { sizeClass, shortHeight } = useSizeClass();
  const loadStatus = useApiLoadingStatus();
  const [authFailed, setAuthFailed] = useState(false);
  const setUp = mapSetUp({ apiKey: GOOGLE_MAPS_API_KEY, loadStatus, authFailed });
  const iconOnly = sizeClass === 'single';
  // Short height tightens the spacing around the header only.
  const spacing = shortHeight ? 4 : 'xs';

  // Google calls this when it rejects the key. vis.gl 1.10.1 never reports that, so the
  // page listens itself. This effect runs before APIProvider's, which starts the load.
  useEffect(() => {
    const page = window as AuthFailureWindow;
    page.gm_authFailure = () => setAuthFailed(true);
    return () => {
      delete page.gm_authFailure;
    };
  }, []);

  return (
    <Stack h="100%" gap={0}>
      {/* Header */}
      <Group component="header" gap="xs" wrap="nowrap" px="md" py={spacing}>
        <HeaderAction
          label="Back"
          icon={IconArrowLeft}
          iconOnly={iconOnly}
          color="gray"
          onClick={handleBack}
        />
        <Title order={5} flex={1} miw={0} lineClamp={1}>
          Run map
        </Title>
        {/* Locate moves the map, so it only shows once the map exists. */}
        {setUp === 'ready' && <LocateButton iconOnly={iconOnly} />}
      </Group>

      {setUp === 'ready' && <RunMapArea split={sizeClass === 'split'} />}
      {setUp === 'no-signal' && (
        <MapNotice message="The map needs signal to load. Your runsheet still works offline.">
          {/* vis.gl 1.10.1 can't retry a failed load in place. Offline, the service worker
              serves the app and the runsheet comes back from localStorage. */}
          <Button color="gray" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </MapNotice>
      )}
      {setUp === 'not-set-up' && <MapNotice message="The Run map isn't set up on this build" />}
    </Stack>
  );
}

// ── Map and sheet ─────────────────────────────────────────────────────────────

function RunMapArea({ split }: { split: boolean }) {
  const { runsheet, stations, setCurrentLine, setCurrent } = useRunsheetContext();
  const { orderOptions, moveToOrder } = useMoveToOrder();
  const [target, setTarget] = useState<SheetTarget | null>(null);
  const [pulse, setPulse] = useState<MarkerPulse | null>(null);
  const { ref: sizeRef, height: sheetHeight } = useElementSize();
  const sheetRef = useRef<HTMLDivElement>(null);
  const mergedSheetRef = useMergedRef(sizeRef, sheetRef);

  const item = sheetItem(target, runsheet?.lines ?? [], stations);
  // A reload that removes the open stop closes its sheet, so it can't reopen if the stop
  // comes back later.
  if (target && !item) setTarget(null);

  // The pulse ends once it has played, so a marker that remounts later, as units do when
  // they fan out or group, doesn't replay it.
  useEffect(() => {
    if (!pulse) return;
    const timer = window.setTimeout(() => setPulse(null), PULSE_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [pulse]);

  const zoomBottom =
    item && !split ? MAP_CLEAR_BAND + sheetHeight + ZOOM_SHEET_GAP : ZOOM_BUTTONS_BOTTOM;

  const handleTapStop = (line: RunsheetLine, offset?: Offset) => {
    setCurrentLine(line.id);
    setCurrent(line);
    const position = stopPosition(line);
    setTarget({ kind: 'stop', id: line.id, marker: position && { position, offset } });
  };

  const handleTapStation = (station: Station) => {
    const position = stopPosition(station);
    setTarget({ kind: 'station', id: station.id, marker: position && { position } });
  };

  const handleMove = (line: RunsheetLine, displayorder: number) => {
    const ids = moveToOrder(line, displayorder);
    setPulse(prev => ({ ids, key: (prev?.key ?? 0) + 1 }));
  };

  return (
    <Box data-testid="run-map" flex={1} mih={0} pos="relative">
      <GoogleMap
        mapId={GOOGLE_MAPS_MAP_ID}
        defaultCenter={DEFAULT_CENTER}
        defaultZoom={DEFAULT_ZOOM}
        // Google's controls are under the Touch floor. Pinch and double-tap zoom still work.
        disableDefaultUI
        // Each new map is a billed load, so the map is kept and reused.
        reuseMaps
        // Google watches its div and redraws at full size when AppShell shows the
        // hidden page again. Item 2 of the device checklist covers a real device.
        style={{ width: '100%', height: '100%' }}
        onClick={() => setTarget(null)}
      >
        <RunMapMarkers pulse={pulse} onTapStop={handleTapStop} onTapStation={handleTapStation} />
      </GoogleMap>
      <NotOnMapMenu onSelect={handleTapStop} />
      <ZoomButtons bottom={zoomBottom} />
      {item && (
        <MapSheet
          ref={mergedSheetRef}
          item={item}
          split={split}
          orderOptions={orderOptions}
          onMove={handleMove}
          onClose={() => setTarget(null)}
        />
      )}
      <PanClear marker={target?.marker ?? null} sheetRef={sheetRef} split={split} />
    </Box>
  );
}

interface PanClearProps {
  marker: TappedMarker | null;
  sheetRef: RefObject<HTMLDivElement | null>;
  split: boolean;
}

/**
 * Pans a tapped marker under the sheet, or within 60px of it, into the middle of the
 * open part of the map. It runs once the sheet has laid out after a tap, and again
 * whenever the sheet or the map changes size, as when a tablet rotates.
 */
function PanClear({ marker, sheetRef, split }: PanClearProps) {
  const map = useMap();

  useEffect(() => {
    const sheet = sheetRef.current;
    if (!map || !marker || !sheet) return;
    const div = map.getDiv();
    const panClear = () => {
      const point = mapPixel(map, marker.position);
      if (!point) return;
      const box = div.getBoundingClientRect();
      const rect = sheet.getBoundingClientRect();
      const by = panClearBy(
        { x: point.x + (marker.offset?.x ?? 0), y: point.y + (marker.offset?.y ?? 0) },
        { width: box.width, height: box.height },
        {
          left: rect.left - box.left,
          top: rect.top - box.top,
          right: rect.right - box.left,
          bottom: rect.bottom - box.top,
        },
        split,
      );
      if (by) map.panBy(by.x, by.y);
    };
    // The observer's first call comes once both have laid out, which covers the tap.
    const observer = new ResizeObserver(panClear);
    observer.observe(sheet);
    observer.observe(div);
    return () => observer.disconnect();
  }, [map, marker, sheetRef, split]);

  return null;
}

// ── Small sub-components ──────────────────────────────────────────────────────

function LocateButton({ iconOnly }: { iconOnly: boolean }) {
  const map = useMap();
  const { location } = useRunsheetLocation();
  // The script can still be loading, and a tap then would do nothing.
  if (!map) return null;

  const handleLocate = () => {
    // No fix yet, or location denied: the camera stays where it is.
    if (!location) {
      notifications.show({ message: 'Location not available' });
      return;
    }
    map.panTo({ lat: location.coords.latitude, lng: location.coords.longitude });
    map.setZoom(STREET_ZOOM);
  };

  return (
    <HeaderAction
      label="Locate"
      icon={IconCurrentLocation}
      iconOnly={iconOnly}
      color="gray"
      onClick={handleLocate}
    />
  );
}

function ZoomButtons({ bottom }: { bottom: number }) {
  const map = useMap();
  if (!map) return null;

  const zoomBy = (step: number) => {
    map.setZoom((map.getZoom() ?? DEFAULT_ZOOM) + step);
  };

  return (
    <Stack pos="absolute" right={MAP_CONTROL_INSET} bottom={bottom} gap="xs">
      <ActionIcon variant="default" aria-label="Zoom in" onClick={() => zoomBy(1)}>
        <IconPlus size={20} />
      </ActionIcon>
      <ActionIcon variant="default" aria-label="Zoom out" onClick={() => zoomBy(-1)}>
        <IconMinus size={20} />
      </ActionIcon>
    </Stack>
  );
}

/** A row opens the stop's sheet, as a marker tap would. */
function NotOnMapMenu({ onSelect }: { onSelect: (line: RunsheetLine) => void }) {
  const { runsheet } = useRunsheetContext();
  const lines = stopsNotOnMap(runsheet?.lines ?? []);
  if (lines.length === 0) return null;

  return (
    <Box pos="absolute" top={MAP_CONTROL_INSET} left={MAP_CONTROL_INSET}>
      <Menu
        position="bottom-start"
        // The list stops above the clear band and scrolls on a short map.
        middlewares={{ size: { padding: { bottom: ZOOM_BUTTONS_BOTTOM, top: 8 } } }}
        // A row is a tap target, so it meets the Touch floor.
        styles={{ item: { minHeight: 44, fontSize: 'var(--mantine-font-size-md)' } }}
      >
        <Menu.Target>
          <Button
            h={44}
            color="gray"
            variant="default"
            rightSection={<IconChevronDown size={18} />}
          >
            {lines.length === 1 ? '1 stop not on the map' : `${lines.length} stops not on the map`}
          </Button>
        </Menu.Target>
        <Menu.Dropdown style={{ overflowY: 'auto' }}>
          {lines.map(line => (
            <Menu.Item key={line.id} onClick={() => onSelect(line)}>
              {stopName(line)}
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>
    </Box>
  );
}

function MapNotice({ message, children }: { message: string; children?: ReactNode }) {
  return (
    <Center flex={1} mih={0} px="md">
      <Stack align="center" gap="md">
        <Text fz="md" ta="center">
          {message}
        </Text>
        {children}
      </Stack>
    </Center>
  );
}
